import { RemoveCircle } from "@mui/icons-material";
import { Grid, GridProps, IconButton } from "@mui/material";
import { observer } from "mobx-react-lite";
import { getNodeId } from "mobx-state-tree";
import { IControl, InputType } from "../models/control";
import { DynamicControl } from "./dynamic-control";

/**
 * Props interface for the DynamicFormArray component
 */
type DynamicFormArrayProps = {
  /** 2D array of child controls organized in rows */
  children: IControl[][];
  /** Optional callback fired when remove button is clicked */
  onRemoveRowClick?: (rowIndex: number) => void;
  /** Grid size for the remove icon button (0-12) */
  removeIconSize?: number;
  /** Additional props passed to the container Grid component */
  containerProps?: GridProps;
  /** Optional callback fired when any control value changes */
  onValueChanged?: (control: IControl, rowIndex: number) => void;
  /** Spacing between controls within a row */
  controlSpacing?: number;
  /** Spacing between rows */
  rowSpacing?: number;
};

/**
 * Component for rendering dynamic arrays of form controls
 * Each row contains a set of controls with optional remove functionality
 * Uses Material-UI Grid system for responsive layout
 *
 * @param children - 2D array of controls to render (rows x controls)
 * @param onRemoveRowClick - Callback for row removal
 * @param removeIconSize - Size allocation for remove button
 * @param containerProps - Additional Grid container properties
 * @param onValueChanged - Callback for value changes with row context
 * @param controlSpacing - Spacing between controls in a row
 * @param rowSpacing - Spacing between rows
 * @returns JSX element containing the rendered form array
 */
export const DynamicFormArray = observer(
  ({
    children,
    onRemoveRowClick,
    removeIconSize = 0.5,
    containerProps,
    onValueChanged,
    controlSpacing = 1,
    rowSpacing = 1,
  }: DynamicFormArrayProps) => {
    return (
      <Grid {...containerProps} container spacing={rowSpacing}>
        {children &&
          children.map((x, rowIndex) => {
            // Generate a unique key for each row
            // Prefer 'id' field if available, otherwise use MobX node ID
            const idCtr = x.filter((x) => x.name === "id")?.[0];
            const rowKey = idCtr?.value || getNodeId(x);

            return (
              <Grid
                data-type={"row"}
                container
                item
                key={rowKey}
                xs={12}
                spacing={1}
              >
                {/* Controls container - takes remaining space after remove button */}
                <Grid
                  container
                  item
                  xs={12 - (onRemoveRowClick ? removeIconSize : 0)}
                  spacing={controlSpacing}
                >
                  {x &&
                    x.map(
                      (c, idx) =>
                        // Only render controls that have a type and are not 'none'
                        c.type &&
                        c.type !== InputType.none && (
                          <DynamicControl
                            onValueChanged={(ctr) => {
                              onValueChanged?.(ctr, rowIndex);
                            }}
                            key={c.name}
                            control={c}
                          ></DynamicControl>
                        )
                    )}
                </Grid>

                {/* Remove button container - only shown if callback provided */}
                {onRemoveRowClick && (
                  <Grid item xs={removeIconSize}>
                    <IconButton
                      onClick={() => {
                        onRemoveRowClick?.(rowIndex);
                      }}
                    >
                      <RemoveCircle></RemoveCircle>
                    </IconButton>
                  </Grid>
                )}
              </Grid>
            );
          })}
      </Grid>
    );
  }
);
